/**
 * The 3D view's camera controls, one home per shell: the drag tool, the
 * camera's glides and its angle (for the view cube), and the 3D quality
 * preference applied to the view. Each root shell provides them once
 * (`View3DProvider`); the view widget's rows and the view cube read and drive
 * them (`useView3D`, `useViewAngle`), and the shell's canvas creates its
 * View3D with `useCreateView3D`, which hands the view to the controls. A new
 * camera button is a control here and a button in the row. A shell opened at
 * a camera angle (`openingAngle`, read once) has its view's first framing look
 * from it.
 */

import type { CameraAngle, TurnDirection } from '@kaiizen/kai-floor-plan-editor-core';
import { useFloorPlanPreferences } from '@kaiizen/kai-floor-plan-editor-core';
import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { View3D, type View3DOptions } from './View3D';
import type { View3DTool } from './viewTools';

export interface View3DControls {
  /** The drag tool; shift swaps to the other for one gesture. */
  tool: View3DTool;
  setTool: (tool: View3DTool) => void;
  /** Glide to look from `angle`, keeping what the camera centers on and its distance. */
  viewFrom: (angle: CameraAngle) => void;
  /** Glide back to the opening view: the whole model framed at the 3/4 angle. */
  goHome: () => void;
  /** Glide a quarter turn about the vertical, the floor staying flat. */
  turnView: (direction: TurnDirection) => void;
  /** Look from `angle` at once, as a drag on the view cube does. */
  setCameraAngle: (angle: CameraAngle) => void;
  /** Which way the camera looks now; null until the view exists. */
  cameraAngle: () => CameraAngle | null;
}

/** The part of a View3D its controls drive. */
type ControlledView = Pick<View3D, 'cameraAngle' | 'goHome' | 'openAt' | 'setCameraAngle' | 'setQuality' | 'setTool' | 'turnView' | 'viewFrom'>;

/** The angle each frame drew, shared with whatever follows the camera (the view cube). */
interface AngleStore {
  angle: CameraAngle | null;
  listeners: Set<() => void>;
}

interface View3DContextValue extends View3DControls {
  /** The canvas hands over its view once it exists, and null once it is disposed. */
  attach: (view: ControlledView | null) => void;
  /** The view reports the angle of each frame that drew a new one. */
  publishAngle: (angle: CameraAngle) => void;
  angleStore: AngleStore;
}

const View3DContext = createContext<View3DContextValue | null>(null);

const useView3DContext = (): View3DContextValue => {
  const value = useContext(View3DContext);
  if (!value) throw new Error('The 3D view controls need a View3DProvider');
  return value;
};

/** Holds a shell's camera controls and keeps its 3D view, once created, in step with them and with the 3D quality preference. */
export function View3DProvider({ children, openingAngle = null }: { children: ReactNode; openingAngle?: CameraAngle | null }): React.JSX.Element {
  const viewRef = useRef<ControlledView | null>(null);
  const openingAngleRef = useRef(openingAngle);
  const angleStoreRef = useRef<AngleStore>({ angle: null, listeners: new Set() });
  const [tool, setTool] = useState<View3DTool>('rotate');
  const { view3dQuality } = useFloorPlanPreferences();
  const latestRef = useRef({ tool, view3dQuality });
  latestRef.current = { tool, view3dQuality };

  useEffect(() => viewRef.current?.setTool(tool), [tool]);
  useEffect(() => viewRef.current?.setQuality(view3dQuality), [view3dQuality]);

  const attach = useCallback((view: ControlledView | null) => {
    viewRef.current = view;
    if (!view) return;
    if (openingAngleRef.current) view.openAt(openingAngleRef.current);
    openingAngleRef.current = null;
    view.setQuality(latestRef.current.view3dQuality);
    view.setTool(latestRef.current.tool);
  }, []);

  const publishAngle = useCallback((angle: CameraAngle) => {
    angleStoreRef.current.angle = angle;
    angleStoreRef.current.listeners.forEach(listener => listener());
  }, []);

  const value = useMemo<View3DContextValue>(() => ({
    tool,
    setTool,
    viewFrom: angle => viewRef.current?.viewFrom(angle),
    goHome: () => viewRef.current?.goHome(),
    turnView: direction => viewRef.current?.turnView(direction),
    setCameraAngle: angle => viewRef.current?.setCameraAngle(angle),
    cameraAngle: () => viewRef.current?.cameraAngle() ?? null,
    attach,
    publishAngle,
    angleStore: angleStoreRef.current,
  }), [tool, attach, publishAngle]);

  return <View3DContext.Provider value={value}>{children}</View3DContext.Provider>;
}

/** The shell's camera controls, for the view widget's rows. */
export const useView3D = (): View3DControls => useView3DContext();

/** Which way the camera looked in the last frame drawn, re-rendering only when that changes; null before the first frame. */
export function useViewAngle(): CameraAngle | null {
  const { angleStore } = useView3DContext();
  const subscribe = useCallback((listener: () => void) => {
    angleStore.listeners.add(listener);
    return () => angleStore.listeners.delete(listener);
  }, [angleStore]);
  return useSyncExternalStore(subscribe, () => angleStore.angle);
}

/**
 * The canvas's View3D: created the first time the canvas shows (no GL context
 * until the user enters 3D) from that render's options, handed to the
 * shell's camera controls, and disposed on unmount. The options are taken
 * once, so their callbacks read the host's latest state through refs.
 */
export function useCreateView3D<S>(visible: boolean, options: View3DOptions<S>) {
  const { attach, publishAngle } = useView3DContext();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const viewRef = useRef<View3D<S> | null>(null);

  useEffect(() => {
    if (!visible || !canvasRef.current || viewRef.current) return;
    viewRef.current = new View3D<S>(canvasRef.current, { ...options, onAngle: publishAngle });
    attach(viewRef.current);
  }, [visible, attach, publishAngle]);

  useEffect(() => () => {
    viewRef.current?.dispose();
    viewRef.current = null;
    attach(null);
  }, [attach]);

  return { canvasRef, viewRef };
}
